import { useReportContext } from '../report-context';
import {
  BacteriaAttention,
  BacteriaAttentionCategory,
  ReportSummary,
} from '@snapi/types';
import { ReportRelatedCard } from './report-related-card';
import { useToggle } from 'react-use';
import { Subtitle } from '@snapi/luxury';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper';
import { Variants } from 'framer-motion';
import Image from 'next/image';
import CheckImg from './images/check.png';
const CardVariants: Variants = {
  open: {
    height: 'auto',
    opacity: 1,
    pointerEvents: 'auto',
  },
  closed: {
    height: 0,
    opacity: 0,
    pointerEvents: 'none',
  },
};

const KeyBacteriaInfo = [
  {
    title: 'What is key bacteria?',
    description:
      'Key bacteria including the most crucial bacteria and can vary with age. Determined exactly which bacteria are crucial for individual age groups. This gives insight into the progress of gut development and makes it possible to track the most influential bacteria.',
  },
  {
    title: 'Why is it important?',
    description:
      'Key bacteria is involved in reducing the risk of health related conditions, proper digestion and absorption of nutrients, and plays a role in healthy growth and development.',
  },
];
const DisruptiveBacteriaInfo = [
  {
    title: 'What is disruptive bacteria?',
    description:
      'Disruptive bacteria is the pathogenic bacteria that may have been acquired through food, water, or exposure to certain lifestyles and environments.',
  },
  {
    title: 'Why is it important?',
    description:
      'When the gut is unbalanced and dominated by disruptive bacteria, it can impact healthy growth and development, and can increase certain health conditions.',
  },
];

interface ReportRelatedConcernBacteriaProps {
  attention: BacteriaAttention[];
  onSelect(bacteria: string): void;
  bacteria: ReportSummary['keyBacteria'];
  condition: string[];
}
export const ReportRelatedConcernBacteria = ({
  attention,
  onSelect,
  bacteria,
  condition,
}: ReportRelatedConcernBacteriaProps) => {
  const { baby } = useReportContext();
  const [onKey, togleKey] = useToggle(false);
  const [onDisruptive, togleDisruptive] = useToggle(false);
  const [onCondition, togleCondition] = useToggle(false);
  const [slideChange, setSlideChange] = useState(false);

  const keyBacterias = attention.filter(
    (bacteria) => bacteria.category === 'key'
  );
  const disruptiveBacterias = attention.filter(
    (bacteria) => bacteria.category === 'disruptive'
  );

  function filterBacteria(objects: BacteriaAttention[], conditions: string[]) {
    const filteredObjects: BacteriaAttention[] = [];

    const conditionObjects = objects.filter(
      (obj) => obj.category === 'condition'
    );

    for (const obj of conditionObjects) {
      // Skip the object if it doesn't match the specified conditions
      if (!conditions.includes(obj.condition ?? '')) {
        continue;
      }

      // Check if the bacteria has already been added for the current condition
      const bacteriaKey = obj.name + obj.condition;
      if (
        !filteredObjects.some(
          (filteredObj) =>
            filteredObj.name + filteredObj.condition === bacteriaKey
        )
      ) {
        filteredObjects.push(obj);
      }
    }

    return filteredObjects;
  }

  const objWithCond = filterBacteria(attention, condition);

  const amountKeyBacteriaMaintain = bacteria.bacteria.filter(
    (bacteria) => bacteria.action === 'maintain'
  ).length;

  const uniqueConditions = [
    ...new Set(objWithCond.map((card) => card.condition)),
  ];

  return (
    <section className="">
      <div className="xl:w-[1200px] xl:mx-auto">
        <div className="px-5 sm:px-8 xl:px-0">
          <h5 className="text-blue-semilight mb-2">insights</h5>
          <h2 className="text-dark mb-10">{baby.name}’s Gut Highlights</h2>
          <div className="flex flex-col gap-10">
            {uniqueConditions.map((condition) => {
              const relatedBacteria = objWithCond.filter(
                (card) => card.condition === condition
              );

              return (
                <div className="" key={condition}>
                  <div className="lg:w-[540px]">
                    <h5 className="border-b-[1px] border-body mb-5">
                      {condition} RELATED BACTERIA
                    </h5>
                    <p className="mb-2">
                      You mentioned that {baby.name} has {condition}. Below you
                      can find the bacteria in {baby.name}'s gut associated with{' '}
                      {condition}.
                    </p>
                    <button
                      onClick={togleCondition}
                      className={clsx(
                        'text-body disabled:text-gray-soft underline mb-5'
                      )}
                    >
                      <Subtitle>
                        {onCondition ? 'Show less' : 'Learn more'}
                      </Subtitle>
                    </button>

                    <div>
                      <motion.div
                        layout
                        animate={onCondition ? 'open' : 'closed'}
                        transition={{ duration: 0.6 }}
                        variants={CardVariants}
                        className="flex-col"
                      >
                        {
                          <div className="flex flex-col gap-2 lg:w-[540px]">
                            <p className="mb-5 sm:mb-10">
                              Displayed here are bacteria closely associated
                              with, known for their potential contribution to
                              the occurrence and development of {condition}{' '}
                              condition. The absence of bacteria in this section
                              indicates that no significant level of these
                              bacteria were detected at the time of the test.
                            </p>
                          </div>
                        }
                      </motion.div>
                    </div>
                  </div>
                  <div className="flex flex-col md:flex-row gap-5">
                    {' '}
                    {relatedBacteria.map((card) => (
                      <ReportRelatedCard
                        key={card.name}
                        {...card}
                        onSelect={onSelect}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          {keyBacterias.length > 0 && (
            <h5 className="border-b-[1px] border-body mb-5 mt-10 lg:w-[540px]">
              KEY BACTERIA
            </h5>
          )}
          {keyBacterias.length === amountKeyBacteriaMaintain && (
            <p className="mb-2 lg:w-[540px]">
              In {baby.name}'s gut, all {amountKeyBacteriaMaintain}{' '}
              <span className="mr-1">out of</span>
              {keyBacterias.length} bacteria important for{' '}
              {baby.gender === 'girl' ? 'her' : 'his'} age are in normal range.
              Great job!
            </p>
          )}
          {keyBacterias.length !== amountKeyBacteriaMaintain &&
            amountKeyBacteriaMaintain === 2 && (
              <p className="mb-2 lg:w-[540px]">
                In {baby.name}'s gut, {amountKeyBacteriaMaintain}{' '}
                <span className="mr-1">out of</span>
                {keyBacterias.length} bacteria important for{' '}
                {baby.gender === 'girl' ? 'her' : 'his'} age are in normal
                range. Let's improve that!
              </p>
            )}
          {keyBacterias.length !== amountKeyBacteriaMaintain &&
            amountKeyBacteriaMaintain < 2 && (
              <p className="mb-2 lg:w-[540px]">
                In {baby.name}'s gut, {amountKeyBacteriaMaintain}{' '}
                <span className="mr-1">out of</span>
                {keyBacterias.length} bacteria important for{' '}
                {baby.gender === 'girl' ? 'her' : 'his'} age is in normal range.
                Let's improve that!
              </p>
            )}

          <button
            onClick={togleKey}
            className={clsx('text-body disabled:text-gray-soft underline mb-5')}
          >
            <Subtitle>{onKey ? 'Show less' : 'Learn more'}</Subtitle>
          </button>
          <motion.div
            layout
            animate={onKey ? 'open' : 'closed'}
            transition={{ duration: 0.6 }}
            variants={CardVariants}
            className="flex-col lg:w-[540px]"
          >
            {KeyBacteriaInfo.map(({ title, description }) => (
              <React.Fragment key={title}>
                <div className="flex flex-col gap-2">
                  <p className="font-semibold">{title}</p>
                  <p className="mb-5 sm:mb-10">{description}</p>
                </div>
              </React.Fragment>
            ))}
          </motion.div>
        </div>
        <div className="flex flex-col xl:flex-row xl:gap-5">
          {' '}
          {attention
            .filter((item) => item.category === BacteriaAttentionCategory.key)
            .map((card) => (
              <div className="mb-2 sm:hidden px-5 xl:flex xl:px-0">
                <ReportRelatedCard
                  key={card.name}
                  {...card}
                  onSelect={onSelect}
                />
              </div>
            ))}
        </div>
        <Swiper
          className="hidden sm:flex pl-8 xl:hidden"
          slidesPerView="auto"
          spaceBetween={20}
          onSlideChange={() => {
            setSlideChange(true);
          }}
          pagination={{
            clickable: true,
            bulletActiveClass: 'bg-body opacity-100',
          }}
          navigation={{
            enabled: true,
            nextEl: '#next-button',
            prevEl: '#prev-button',
          }}
          modules={[Navigation, Pagination]}
        >
          <>
            {attention
              .filter((item) => item.category === BacteriaAttentionCategory.key)
              .map((item) => {
                return (
                  <SwiperSlide
                    key={item.name}
                    className="mb-12 flex justify-center sm:w-[380px]"
                  >
                    {(data) => {
                      return (
                        <div className="flex flex-col justify-start items-center rounded-[20px] pl-0">
                          <ReportRelatedCard
                            key={item.name}
                            {...item}
                            onSelect={onSelect}
                          />
                        </div>
                      );
                    }}
                  </SwiperSlide>
                );
              })}
          </>
        </Swiper>
        {disruptiveBacterias.length > 0 ? (
          <div className="px-5 sm:px-8 xl:px-0 lg:w-[572px] lg:pr-0 xl:w-[540px]">
            <h5 className="border-b-[1px] border-body mb-5 mt-10">
              DISRUPTIVE BACTERIA
            </h5>
            <p className="mb-2">
              {baby.name} has {disruptiveBacterias.length} disruptive bacteria
              outside of normal ranges. Don’t be alarmed! We’ll work on this.
            </p>
          </div>
        ) : (
          <div className="px-5 sm:px-8 xl:px-0 lg:w-[572px] lg:pr-0 xl:w-[540px]">
            <h5 className="border-b-[1px] border-body mb-5 mt-10">
              DISRUPTIVE BACTERIA
            </h5>
            <p className="mb-2">
              {baby.name}'s gut contains disruptive bacteria that are within
              normal ranges. This is healthy!
            </p>
            {/* <div className='flex gap-3 bg-green-light px-5 py-3 rounded-[10px] mt-5 max-w-[540px] mb-2'>
             <div className='flex items-center justify-center shrink-0'> <Image src={CheckImg} width={28} height={28} /></div>
              <p className='font-semibold'>We’re impressed! Wesley currently does not have any disruptive bacteria that requires additional monitoring.</p>
            </div>     */}
          </div>
        )}
        <button
          onClick={togleDisruptive}
          className={clsx(
            'text-body disabled:text-gray-soft underline mb-5 px-5 sm:px-8 xl:px-0'
          )}
        >
          <Subtitle>{onDisruptive ? 'Show less' : 'Learn more'}</Subtitle>
        </button>

        <div className="px-5 sm:px-8 xl:px-0">
          <motion.div
            layout
            animate={onDisruptive ? 'open' : 'closed'}
            transition={{ duration: 0.6 }}
            variants={CardVariants}
            className="flex-col"
          >
            {DisruptiveBacteriaInfo.map(({ title, description }) => (
              <React.Fragment key={title}>
                <div className="flex flex-col gap-2 lg:w-[540px]">
                  <p className="font-semibold">{title}</p>
                  <p className="mb-5 sm:mb-10">{description}</p>
                </div>
              </React.Fragment>
            ))}
          </motion.div>
        </div>
        {disruptiveBacterias.length <= 0 ? (
          <div className="px-5 sm:px-8 xl:px-0 lg:w-[572px] lg:pr-0 xl:w-[540px]">
            <div className="flex gap-3 bg-green-light px-5 py-3 rounded-[10px] mb-2">
              <div className="flex items-center justify-center shrink-0">
                {' '}
                <Image src={CheckImg} width={28} height={28} />
              </div>
              <p className="font-semibold">
                We’re impressed! {baby.name} currently does not have any
                disruptive bacteria that requires additional monitoring.
              </p>
            </div>
          </div>
        ) : null}
        <div className="flex flex-col px-5 xl:flex-row xl:gap-5 xl:px-0">
          {attention
            .filter(
              (item) => item.category === BacteriaAttentionCategory.disruptive
            )
            .map((card) => (
              <div className="mb-2 sm:hidden xl:flex">
                <ReportRelatedCard
                  key={card.name}
                  {...card}
                  onSelect={onSelect}
                />
              </div>
            ))}
        </div>
        <Swiper
          className="hidden sm:flex pl-8 xl:hidden"
          slidesPerView="auto"
          spaceBetween={20}
          onSlideChange={() => {
            setSlideChange(true);
          }}
          pagination={{
            clickable: true,
            bulletActiveClass: 'bg-body opacity-100',
          }}
          navigation={{
            enabled: true,
            nextEl: '#next-button',
            prevEl: '#prev-button',
          }}
          modules={[Navigation, Pagination]}
        >
          <>
            {attention
              .filter(
                (item) => item.category === BacteriaAttentionCategory.disruptive
              )
              .map((item) => {
                return (
                  <SwiperSlide
                    key={item.name}
                    className="mb-12 flex justify-center sm:w-[380px]"
                  >
                    {(data) => {
                      return (
                        <div className="flex flex-col justify-start items-center rounded-[20px] pl-0">
                          <ReportRelatedCard
                            key={item.name}
                            {...item}
                            onSelect={onSelect}
                          />
                        </div>
                      );
                    }}
                  </SwiperSlide>
                );
              })}
          </>
        </Swiper>
      </div>
    </section>
  );
};
